import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  CancelRunArgs,
  ConfirmColumnsArgs,
  ProductSearchArgs,
  ProductSearchResponse,
  RematchArgs,
  RematchResponse,
  RunDetailArgs,
  RunDetailResponse,
  RunHistoryArgs,
  RunHistoryResponse,
  RunTotals,
} from './types';
import { normalizeDecision, normalizeVerdict } from '../lib/runEvents';
import type {
  DecideGroupArgs,
  DecideGroupResponse,
  GroupRowsArgs,
  GroupRowsResponse,
  RunGroupItem,
  RunGroupsArgs,
  RunGroupsResponse,
} from './groupTypes';

/**
 * Pure request builders — exported so the exact wire (URL + params + body) is
 * lockable in a `toEqual` unit test (`fetchBaseQuery` can't be intercepted, so
 * this is how the request shape is pinned). Endpoints:
 *   GET   /sku/v1/productMatchings/run/history?page&pageSize[&entityId][&entityType]
 *   GET   /sku/v1/productMatchings/run/history/{runId}?[cursor&]pageSize
 *   POST  /sku/v1/productMatchings/run/{runId}/cancel        body {}
 *   POST  /sku/v1/productMatchings/run/{runId}/rematch       body { productId, lineIdxs }
 *   PATCH /sku/v1/productMatchings/run/{runId}/columnResolution  body { columnResolution }
 *   GET   /sku/v1/productMatchings/searchCatalog?q&limit
 */
export const productMatchingRequestBuilders = {
  getRunHistory: ({ page = 1, pageSize = 10, entityId, entityType }: RunHistoryArgs) => {
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('pageSize', String(pageSize));
    if (entityId) qs.set('entityId', entityId);
    if (entityType) qs.set('entityType', entityType);
    return {
      url: `${services.sku}/v1/productMatchings/run/history?${qs.toString()}`,
      method: 'GET' as const,
    };
  },
  getRunDetail: ({ runId, cursor, pageSize = 500 }: RunDetailArgs) => {
    const qs = new URLSearchParams();
    // Keyset pagination: no cursor reads the first page; each response's nextCursor
    // is echoed back to seek the following page (no page numbers on this endpoint).
    if (cursor) qs.set('cursor', cursor);
    qs.set('pageSize', String(pageSize));
    return {
      url: `${services.sku}/v1/productMatchings/run/history/${encodeURIComponent(runId)}?${qs.toString()}`,
      method: 'GET' as const,
    };
  },
  cancelRun: ({ runId }: CancelRunArgs) => ({
    url: `${services.sku}/v1/productMatchings/run/${encodeURIComponent(runId)}/cancel`,
    method: 'POST' as const,
    body: {},
  }),
  // Contract per SkuMapping.API RematchRunRows (docs/productMatching/rematch-api.md):
  // POST body { productId, lineIdxs } → 200 { runId, productId, results, totals };
  // 409 while the run is live, 400 for unknown line indexes / variant-less products.
  rematchRows: ({ runId, productId, lineIdxs }: RematchArgs) => ({
    url: `${services.sku}/v1/productMatchings/run/${encodeURIComponent(runId)}/rematch`,
    method: 'POST' as const,
    body: { productId, lineIdxs },
  }),
  confirmColumns: ({ runId, columnResolution, vocabulary }: ConfirmColumnsArgs) => ({
    url: `${services.sku}/v1/productMatchings/run/${encodeURIComponent(runId)}/columnResolution`,
    method: 'PATCH' as const,
    body: { columnResolution, ...(vocabulary ? { vocabulary } : {}) },
  }),
  searchProducts: ({ q, limit = 20 }: ProductSearchArgs) => {
    const qs = new URLSearchParams();
    qs.set('q', q);
    qs.set('limit', String(limit));
    return {
      url: `${services.sku}/v1/productMatchings/searchCatalog?${qs.toString()}`,
      method: 'GET' as const,
    };
  },
  // Contract per SkuMapping.API GetProductGroups (product-first review flow):
  // GET /sku/v1/productMatchings/run/{runId}/groups?cursor&pageSize
  //   → { items, total, pageSize, nextCursor } (keyset walk, like the run-detail rows).
  getRunGroups: ({ runId, cursor, pageSize = 500 }: RunGroupsArgs) => {
    const qs = new URLSearchParams();
    if (cursor) qs.set('cursor', cursor);
    qs.set('pageSize', String(pageSize));
    return {
      url: `${services.sku}/v1/productMatchings/run/${encodeURIComponent(runId)}/groups?${qs.toString()}`,
      method: 'GET' as const,
    };
  },
  // Contract per SkuMapping.API GetProductGroupRows (product-first lazy rows):
  // GET /sku/v1/productMatchings/run/{runId}/groups/rows?groupKey&page&pageSize
  //   → { groupKey, memberLineIdxs, items, total, pageSize, nextCursor }.
  // The groupKey rides the QUERY STRING (URL-encoded) — encoded slashes survive there,
  // unlike in a path segment (see decideGroup below).
  getGroupRows: ({ runId, groupKey, cursor, pageSize = 1000 }: GroupRowsArgs) => {
    const qs = new URLSearchParams();
    qs.set('groupKey', groupKey);
    if (cursor) qs.set('cursor', cursor);
    qs.set('pageSize', String(pageSize));
    return {
      url: `${services.sku}/v1/productMatchings/run/${encodeURIComponent(runId)}/groups/rows?${qs.toString()}`,
      method: 'GET' as const,
    };
  },
  // Contract per SkuMapping.API GetRunTotals:
  // GET /sku/v1/productMatchings/run/{runId}/totals → { runId, totals } — the run's
  // status totals recounted server-side. Decisions no longer echo totals; the portal
  // reads this once its decision(s) have landed.
  getRunTotals: ({ runId }: { runId: string }) => ({
    url: `${services.sku}/v1/productMatchings/run/${encodeURIComponent(runId)}/totals`,
    method: 'GET' as const,
  }),
  // Contract per SkuMapping.API DecideProductGroup:
  // POST /sku/v1/productMatchings/run/{runId}/groups/decision
  //   body { groupKey, decision, productId?, lineIdxs?, dryRun? } → { runId, groupKey, group, results, totals };
  // 409 when the group already carries a decision, 400 for select without productId.
  // groupKey is an opaque server key ("g:{cell}" / "t:{sha}") — never parsed. It travels
  // in the body: the raw vendor cell it embeds can contain '/', and an encoded slash in
  // a path segment is rewritten or rejected by some proxy/ingress hops.
  decideGroup: ({ runId, groupKey, decision, productId, lineIdxs, dryRun }: DecideGroupArgs) => ({
    url: `${services.sku}/v1/productMatchings/run/${encodeURIComponent(runId)}/groups/decision`,
    method: 'POST' as const,
    body: {
      groupKey,
      decision,
      ...(productId ? { productId } : {}),
      ...(lineIdxs ? { lineIdxs } : {}),
      ...(dryRun ? { dryRun } : {}),
    },
  }),
};

/** Tolerate a defensive `{ data: {...} }` wrapper (other portal services ship both). */
function unwrap<T>(response: T | { data?: T } | unknown): T | undefined {
  const r = response as { data?: T };
  if (r && typeof r === 'object' && 'data' in r && r.data != null) return r.data;
  return response as T;
}

/** Unwrap the run-history envelope; default to an empty page on an unexpected shape. */
export function unwrapRunHistory(response: unknown): RunHistoryResponse {
  const d = unwrap<RunHistoryResponse>(response);
  const items = Array.isArray(d?.items) ? d!.items : [];
  return {
    items,
    total: d?.total ?? items.length,
    page: d?.page ?? 1,
    pageSize: d?.pageSize ?? 10,
  };
}

/** Unwrap one page of a run's stored result rows. */
export function unwrapRunDetail(response: unknown): RunDetailResponse {
  const d = unwrap<RunDetailResponse>(response);
  const items = Array.isArray(d?.items) ? d!.items : [];
  return {
    run: d?.run ?? ({ id: '', status: 'completed' } as RunDetailResponse['run']),
    columnResolution: d?.columnResolution ?? null,
    items,
    total: d?.total ?? items.length,
    pageSize: d?.pageSize ?? 500,
    // Absent normalizes to null — "no further pages to seek".
    nextCursor: d?.nextCursor ?? null,
  };
}

/** Unwrap the catalog-search envelope. */
export function unwrapProductSearch(response: unknown): ProductSearchResponse {
  const d = unwrap<ProductSearchResponse>(response);
  return { results: Array.isArray(d?.results) ? d!.results : [] };
}

/** Unwrap the re-match envelope; a junk shape normalizes to zero rewritten rows. */
export function unwrapRematch(response: unknown): RematchResponse {
  const d = unwrap<RematchResponse>(response);
  return {
    runId: d?.runId ?? '',
    productId: d?.productId ?? '',
    results: Array.isArray(d?.results) ? d!.results : [],
    totals: d?.totals ?? null,
  };
}

// The server stores group verdict/decision enums PascalCase ("Matched"/"Pending") while
// the SSE frames ship lowercase — normalize once at the boundary so every consumer
// compares one vocabulary. Same fold as the SSE mapper: an unknown value from a newer
// server lands as 'pending' (neutral badge) instead of escaping the union and crashing
// the badge's detail lookup.
function normalizeGroup(item: RunGroupItem): RunGroupItem {
  return {
    ...item,
    verdict: normalizeVerdict(item.verdict),
    decisionState: normalizeDecision(item.decisionState),
    candidates: Array.isArray(item.candidates) ? item.candidates : [],
  };
}

/** Unwrap one page of a run's product groups; junk shapes normalize to an empty page. */
export function unwrapRunGroups(response: unknown): RunGroupsResponse {
  const d = unwrap<RunGroupsResponse>(response);
  const items = Array.isArray(d?.items) ? d!.items.map(normalizeGroup) : [];
  return {
    items,
    total: d?.total ?? items.length,
    pageSize: d?.pageSize ?? 500,
    nextCursor: d?.nextCursor ?? null,
  };
}

/** Unwrap one group's member-rows page; junk shapes normalize to an empty result. */
export function unwrapRunTotals(response: unknown): RunTotals | null {
  const d = unwrap<{ totals?: RunTotals | null }>(response);
  return d?.totals ?? null;
}

export function unwrapGroupRows(response: unknown): GroupRowsResponse {
  const d = unwrap<GroupRowsResponse>(response);
  const items = Array.isArray(d?.items) ? d!.items : [];
  return {
    groupKey: d?.groupKey ?? '',
    memberLineIdxs: Array.isArray(d?.memberLineIdxs) ? d!.memberLineIdxs : [],
    items,
    total: d?.total ?? items.length,
    pageSize: d?.pageSize ?? 1000,
    nextCursor: d?.nextCursor ?? null,
  };
}

/** Unwrap the group-decision envelope (rematch-shaped: rewritten rows + totals). */
export function unwrapDecideGroup(response: unknown): DecideGroupResponse {
  const d = unwrap<DecideGroupResponse>(response);
  const results = Array.isArray(d?.results) ? d!.results : [];
  return {
    runId: d?.runId ?? '',
    groupKey: d?.groupKey ?? '',
    group: d?.group ? normalizeGroup(d.group) : null,
    results,
    rewrittenRows: d?.rewrittenRows ?? results.length,
    resultsTruncated: Boolean(d?.resultsTruncated),
    flaggedLineIdxs: Array.isArray(d?.flaggedLineIdxs) ? d!.flaggedLineIdxs : [],
    totals: d?.totals ?? null,
    runCompleted: Boolean(d?.runCompleted),
  };
}

/**
 * AI Product Mapping slice (super-admin surface, flag `omProd722AiCatalogMappingAssistant`).
 * Holds the request/response calls (history, detail, cancel, confirm, search). The
 * SSE `POST .../run` stream is NOT here —
 * it can't be expressed as an RTK Query request/response endpoint (mid-stream
 * pause/resume + batched client merge); it lives in the run-stream thunk.
 */
export const productMatchingApi = createApi({
  reducerPath: 'productMatchingApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['ProductMatchingRun'],
  endpoints: (builder) => ({
    // GET /sku/v1/productMatchings/run/history?page&pageSize[&entityId][&entityType]
    getRunHistory: builder.query<RunHistoryResponse, RunHistoryArgs>({
      query: productMatchingRequestBuilders.getRunHistory,
      transformResponse: unwrapRunHistory,
      providesTags: [{ type: 'ProductMatchingRun', id: 'HISTORY' }],
    }),
    // GET /sku/v1/productMatchings/run/history/{runId}?[cursor&]pageSize
    // One page; the restore flow walks nextCursor via the run-stream thunk.
    getRunDetail: builder.query<RunDetailResponse, RunDetailArgs>({
      query: productMatchingRequestBuilders.getRunDetail,
      transformResponse: unwrapRunDetail,
      providesTags: (_result, _error, { runId }) => [{ type: 'ProductMatchingRun', id: runId }],
    }),
    // POST /sku/v1/productMatchings/run/{runId}/cancel — refreshes the history list.
    cancelRun: builder.mutation<void, CancelRunArgs>({
      query: productMatchingRequestBuilders.cancelRun,
      invalidatesTags: [{ type: 'ProductMatchingRun', id: 'HISTORY' }],
    }),
    // POST /sku/v1/productMatchings/run/{runId}/rematch — the grouped bulk edit. The
    // grid patches rows from the response; the invalidations keep the history list's
    // totals and any cached detail pages from serving pre-re-match data.
    rematchRows: builder.mutation<RematchResponse, RematchArgs>({
      query: productMatchingRequestBuilders.rematchRows,
      transformResponse: unwrapRematch,
      invalidatesTags: (_result, _error, { runId }) => [
        { type: 'ProductMatchingRun', id: 'HISTORY' },
        { type: 'ProductMatchingRun', id: runId },
      ],
    }),
    // PATCH /sku/v1/productMatchings/run/{runId}/columnResolution — resumes a paused run.
    confirmColumns: builder.mutation<unknown, ConfirmColumnsArgs>({
      query: productMatchingRequestBuilders.confirmColumns,
    }),
    // GET /sku/v1/productMatchings/searchCatalog?q&limit — manual SKU picker.
    searchProducts: builder.query<ProductSearchResponse, ProductSearchArgs>({
      query: productMatchingRequestBuilders.searchProducts,
      transformResponse: unwrapProductSearch,
    }),
    // GET /sku/v1/productMatchings/run/{runId}/groups — the product-first restore read.
    getRunGroups: builder.query<RunGroupsResponse, RunGroupsArgs>({
      query: productMatchingRequestBuilders.getRunGroups,
      transformResponse: unwrapRunGroups,
      providesTags: (_result, _error, { runId }) => [{ type: 'ProductMatchingRun', id: `${runId}:groups` }],
    }),
    // GET /sku/v1/productMatchings/run/{runId}/groups/rows — one group's member rows,
    // fetched when the reviewer expands the group (the stream carries no row frames on
    // product-first runs).
    getGroupRows: builder.query<GroupRowsResponse, GroupRowsArgs>({
      query: productMatchingRequestBuilders.getGroupRows,
      transformResponse: unwrapGroupRows,
      providesTags: (_result, _error, { runId }) => [{ type: 'ProductMatchingRun', id: `${runId}:groups` }],
    }),
    // GET /sku/v1/productMatchings/run/{runId}/totals — the recounted run totals,
    // read after decisions land instead of riding every decision response.
    getRunTotals: builder.query<RunTotals | null, { runId: string }>({
      query: productMatchingRequestBuilders.getRunTotals,
      transformResponse: unwrapRunTotals,
    }),
    // POST /sku/v1/productMatchings/run/{runId}/groups/decision — the per-group
    // review action. The grid patches rows from the response (like rematch); invalidations
    // keep history totals, cached detail pages and the groups page fresh.
    decideGroup: builder.mutation<DecideGroupResponse, DecideGroupArgs>({
      query: productMatchingRequestBuilders.decideGroup,
      transformResponse: unwrapDecideGroup,
      invalidatesTags: (_result, _error, { runId, dryRun }) =>
        // A dry run previews without writing — nothing cached went stale.
        dryRun
          ? []
          : [
              { type: 'ProductMatchingRun', id: 'HISTORY' },
              { type: 'ProductMatchingRun', id: runId },
              { type: 'ProductMatchingRun', id: `${runId}:groups` },
            ],
    }),
  }),
});

export const {
  useGetRunHistoryQuery,
  useLazyGetRunHistoryQuery,
  useGetRunDetailQuery,
  useLazyGetRunDetailQuery,
  useCancelRunMutation,
  useRematchRowsMutation,
  useConfirmColumnsMutation,
  useSearchProductsQuery,
  useLazySearchProductsQuery,
  useLazyGetRunGroupsQuery,
  useDecideGroupMutation,
} = productMatchingApi;
